import React, { useContext, useMemo, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { DataContext } from '../../Provider/dataProvider';
import '../../css/homePages.css';
import { fetchWithAuth } from '../../utils/authUtils';
import { useFeatureAccess } from '../../hooks/useFeatureAccess';
import { FEATURES } from '../../constants/featureAccess';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

const HomeRelative = () => {
    const { data, accessToken, authReady } = useContext(DataContext);
    const { canUse, loading: accessLoading } = useFeatureAccess();

    const dashboardName = useMemo(() => {
        return data?.fullName || data?.full_name || data?.username || 'Người dùng';
    }, [data]);

    const [todayStatuses, setTodayStatuses] = useState([]);
    const [checkinStatuses, setCheckinStatuses] = useState([]);
    const [relatives, setRelatives] = useState([]);
    const [warningCount, setWarningCount] = useState(0);

    useEffect(() => {
        if (!authReady || !accessToken || accessLoading) return;
        const loadStatuses = async () => {
            if (!canUse(FEATURES.HEALTH_MONITOR)) {
                setTodayStatuses([]);
                return;
            }
            try {
                const res = await fetchWithAuth(`${API_BASE}/api/health-logs/today-status`);
                const payload = await res.json().catch(() => ({}));
                if (res.ok && payload?.success && Array.isArray(payload?.data)) {
                    setTodayStatuses(payload.data);
                } else {
                    setTodayStatuses([]);
                }
            } catch (_) {
                setTodayStatuses([]);
            }
        };
        const loadRelatives = async () => {
            try {
                const res = await fetchWithAuth(`${API_BASE}/relatives`);
                const payload = await res.json().catch(() => ({}));
                if (res.ok && payload?.success && Array.isArray(payload?.data)) {
                    setRelatives(payload.data);
                } else {
                    setRelatives([]);
                }
            } catch (_) {
                setRelatives([]);
            }
        };
        const loadUnreadAlerts = async () => {
            if (!canUse(FEATURES.ALERTS)) {
                setWarningCount(0);
                return;
            }
            try {
                const res = await fetchWithAuth(`${API_BASE}/api/alerts?unread=1&limit=100`);
                const payload = await res.json().catch(() => ({}));
                if (res.ok && payload?.success && Array.isArray(payload?.data)) {
                    setWarningCount(Number(payload.data.length || 0));
                } else {
                    setWarningCount(0);
                }
            } catch (_) {
                setWarningCount(0);
            }
        };
        const loadCheckinStatuses = async () => {
            if (!canUse(FEATURES.DAILY_CHECKIN)) {
                setCheckinStatuses([]);
                return;
            }
            try {
                const res = await fetchWithAuth(`${API_BASE}/api/daily-checkin/status`);
                const payload = await res.json().catch(() => ({}));
                if (res.ok && payload?.success && Array.isArray(payload?.data)) {
                    setCheckinStatuses(payload.data);
                } else {
                    setCheckinStatuses([]);
                }
            } catch (_) {
                setCheckinStatuses([]);
            }
        };
        loadStatuses();
        loadCheckinStatuses();
        loadRelatives();
        loadUnreadAlerts();
    }, [authReady, accessToken, accessLoading, canUse]);

    const checkinRows = useMemo(() => {
        const map = new Map((checkinStatuses || []).map((x) => [Number(x.id), x]));
        return (relatives || []).map((e) => {
            const s = map.get(Number(e.id));
            return {
                id: Number(e.id),
                fullName: e.fullName || e.username || '',
                linkKey: e.linkKey || '---',
                checkedInToday: Boolean(s?.checkedInToday),
                consecutiveMissedDays: Number(s?.consecutiveMissedDays || 0),
            };
        });
    }, [relatives, checkinStatuses]);

    const rows = useMemo(() => {
        const map = new Map((todayStatuses || []).map((x) => [Number(x.id), x]));
        return (relatives || []).map((e) => {
            const s = map.get(Number(e.id));
            return {
                id: Number(e.id),
                fullName: e.fullName || e.username || '',
                linkKey: e.linkKey || '---',
                updatedToday: Boolean(s?.updatedToday),
            };
        });
    }, [relatives, todayStatuses]);

    const trackedCount = rows.length;

    return (
        <div className="tamcare-home-page">
            <div className="tamcare-home-shell">
                <div className="tamcare-home-header tamcare-fade-up">
                    <div>
                        <h1 className="tamcare-home-title">Xin chào, {dashboardName} 👋</h1>
                        <p className="tamcare-home-subtitle">Hôm nay người thân của bạn thế nào?</p>
                    </div>
                    <Link to="/my-relatives" className="tamcare-btn-connect">
                        + Kết nối hồ sơ mới
                    </Link>
                </div>

                <div className="tamcare-stat-grid tamcare-fade-up">
                    <div className="tamcare-stat-card">
                        <div className="tamcare-stat-icon" style={{ background: '#e0effa', color: '#2c5282' }}>
                            <i className="fa-solid fa-user-check"></i>
                        </div>
                        <div>
                            <small>Đang theo dõi</small>
                            <h2>{trackedCount} Người thân</h2>
                        </div>
                    </div>

                    <div className="tamcare-stat-card">
                        <div className="tamcare-stat-icon" style={{ background: '#fff5f2', color: '#ef4444' }}>
                            <i className="fa-solid fa-triangle-exclamation"></i>
                        </div>
                        <div>
                            <small>Cảnh báo mới</small>
                            <h2 style={{ color: '#ef4444' }}>{warningCount.toString().padStart(2, '0')} Tin khẩn</h2>
                        </div>
                    </div>
                </div>

                <div className="tamcare-table-section tamcare-fade-up">
                    <h3><i className="fa-solid fa-clipboard-check"></i> Trạng thái điểm danh hôm nay</h3>
                    <table className="tamcare-table">
                        <thead>
                            <tr>
                                <th>Người thân</th>
                                <th>Mã kết nối</th>
                                <th>Điểm danh</th>
                                <th>Ghi chú</th>
                                <th>Hành động</th>
                            </tr>
                        </thead>
                        <tbody>
                            {checkinRows.length > 0 ? (
                                checkinRows.map((item, index) => {
                                    const done = Boolean(item.checkedInToday);
                                    const missed = item.consecutiveMissedDays || 0;
                                    const elderlyId = item?.id;
                                    const elderlyName = item?.fullName || `Người thân ${index + 1}`;
                                    const linkKey = item?.linkKey || '---';
                                    const note = done
                                        ? 'Đã điểm danh hôm nay.'
                                        : missed >= 3
                                            ? `Chưa điểm danh ${missed} ngày liên tiếp — hãy hỏi thăm.`
                                            : 'Chưa điểm danh hôm nay.';

                                    return (
                                        <tr key={`checkin-${elderlyId || index}`}>
                                            <td style={{ fontWeight: 700 }}>{elderlyName}</td>
                                            <td>
                                                <code style={{ background: '#eef2f6', padding: '4px 8px', borderRadius: 6 }}>{linkKey}</code>
                                            </td>
                                            <td>
                                                <span
                                                    className="tamcare-status-pill"
                                                    style={done ? { background: '#e6f7ef', color: '#16a34a' } : { background: '#fff9e6', color: '#d97706' }}
                                                >
                                                    {done ? 'ĐÃ ĐIỂM DANH' : 'CHƯA ĐIỂM DANH'}
                                                </span>
                                            </td>
                                            <td>{note}</td>
                                            <td>
                                                {elderlyId && (
                                                    <Link
                                                        to={`/profile/${elderlyId}`}
                                                        style={{
                                                            padding: '6px 12px',
                                                            borderRadius: '6px',
                                                            background: '#0f766e',
                                                            color: '#ffffff',
                                                            textDecoration: 'none',
                                                            fontSize: '12px',
                                                            fontWeight: '600',
                                                            whiteSpace: 'nowrap'
                                                        }}
                                                    >
                                                        Xem hồ sơ
                                                    </Link>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan="5" style={{ textAlign: 'center', padding: '30px' }}>Chưa có người thân liên kết.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <div className="tamcare-table-section tamcare-fade-up">
                    <h3><i className="fa-solid fa-heart-pulse"></i> Tình trạng cập nhật chỉ số hôm nay</h3>
                    <table className="tamcare-table">
                        <thead>
                            <tr>
                                <th>Người thân</th>
                                <th>Mã kết nối</th>
                                <th>Chỉ số hôm nay</th>
                                <th>Ghi chú</th>
                                <th>Hành động</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.length > 0 ? (
                                rows.map((item, index) => {
                                    const done = Boolean(item.updatedToday);
                                    const elderlyId = item?.id;
                                    const elderlyName = item?.fullName || `Người thân ${index + 1}`;
                                    const linkKey = item?.linkKey || '---';
                                    const note = done ? 'Đã có chỉ số hôm nay.' : 'Chưa có chỉ số hôm nay.';

                                    return (
                                        <tr key={elderlyId || index}>
                                            <td style={{ fontWeight: 700 }}>{elderlyName}</td>
                                            <td>
                                                <code style={{ background: '#eef2f6', padding: '4px 8px', borderRadius: 6 }}>{linkKey}</code>
                                            </td>
                                            <td>
                                                <span
                                                    className="tamcare-status-pill"
                                                    style={done ? { background: '#e6f7ef', color: '#16a34a' } : { background: '#fff9e6', color: '#d97706' }}
                                                >
                                                    {done ? 'ĐÃ CẬP NHẬT' : 'CHƯA CẬP NHẬT'}
                                                </span>
                                            </td>
                                            <td>{note}</td>
                                            <td style={{ display: 'flex', gap: '8px' }}>
                                                {elderlyId && (
                                                    <>
                                                        <Link 
                                                            to={`/medical-history/${elderlyId}`}
                                                            style={{
                                                                padding: '6px 12px',
                                                                borderRadius: '6px',
                                                                background: '#f39c12',
                                                                color: '#ffffff',
                                                                textDecoration: 'none',
                                                                fontSize: '12px',
                                                                fontWeight: '600',
                                                                whiteSpace: 'nowrap'
                                                            }}
                                                        >
                                                            📋 Hồ sơ
                                                        </Link>
                                                        <Link 
                                                            to={`/health/${elderlyId}`}
                                                            style={{
                                                                padding: '6px 12px',
                                                                borderRadius: '6px',
                                                                background: '#8b5cf6',
                                                                color: '#ffffff',
                                                                textDecoration: 'none',
                                                                fontSize: '12px',
                                                                fontWeight: '600',
                                                                whiteSpace: 'nowrap'
                                                            }}
                                                        >
                                                            📅 Nhập chỉ số
                                                        </Link>
                                                    </>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan="5" style={{ textAlign: 'center', padding: '30px' }}>Chưa có người thân liên kết.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default HomeRelative;